import { FC, useEffect, useMemo, useState } from 'react'
import { LuSearch, LuX } from 'react-icons/lu'
import { Link, useSearchParams } from 'react-router-dom'
import { contentApi } from '@/api'
import { getSeoContent } from '@/content/siteContent'
import { useI18n } from '@/hooks'
import { usePageMetadata } from '@/seo/usePageMetadata'
import type { LearnArticle, LearnPageContent, LearnSectionId } from '@/types/content'

const learnSectionIds: LearnSectionId[] = ['tea-knowledge', 'meditation', 'life']

const isLearnSectionId = (value: string | null): value is LearnSectionId =>
  value !== null && learnSectionIds.includes(value as LearnSectionId)

const formatPublishedDate = (value: string) => {
  const date = new Date(`${value}T00:00:00`)

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

const getCollectionArticles = (content: LearnPageContent): LearnArticle[] => [
  ...content.meditation.posts,
  ...content.life.posts,
  ...content.teaKnowledge.posts,
]

const getSortedUniqueArticles = (content: LearnPageContent): LearnArticle[] => {
  const seen = new Set<string>()

  return getCollectionArticles(content)
    .sort((current, next) => next.publishedAt.localeCompare(current.publishedAt))
    .filter(article => {
      if (seen.has(article.id)) {
        return false
      }

      seen.add(article.id)
      return true
    })
}

const articleMatchesSearch = (article: LearnArticle, query: string) => {
  const normalizedQuery = query.trim().toLowerCase()

  if (!normalizedQuery) {
    return true
  }

  return [
    article.title,
    article.excerpt,
    article.publishedAt,
    ...article.categories,
  ].some(value => value.toLowerCase().includes(normalizedQuery))
}

const LearnPage: FC = () => {
  const { lang, fontClass, locale } = useI18n()
  const [content, setContent] = useState<LearnPageContent | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeArticleId, setActiveArticleId] = useState<string | null>(null)
  const [activeSectionId, setActiveSectionId] = useState<LearnSectionId | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const seo = content?.seo ?? getSeoContent(lang).learn

  useEffect(() => {
    let cancelled = false

    const loadLearnContent = async () => {
      const response = await contentApi.getLearnPage(lang)

      if (!cancelled) {
        setContent(response)
      }
    }

    void loadLearnContent()

    return () => {
      cancelled = true
    }
  }, [lang])

  const allArticles = useMemo(() => content ? getSortedUniqueArticles(content) : [], [content])

  useEffect(() => {
    if (!content || allArticles.length === 0) {
      return
    }

    const requestedArticle = searchParams.get('article')
    const requestedSection = searchParams.get('section')
    const sectionId = isLearnSectionId(requestedSection) ? requestedSection : 'all'
    const sectionArticles = sectionId === 'all'
      ? allArticles
      : allArticles.filter(article => article.categories.some(category =>
          category.toLowerCase().replace(/\s+/g, '-') === sectionId
        ))
    const article = allArticles.find(item => item.id === requestedArticle) ?? sectionArticles[0] ?? allArticles[0]

    setActiveSectionId(sectionId)
    setActiveArticleId(article.id)
  }, [allArticles, content, searchParams])

  const visibleArticles = useMemo(() => {
    const categoryFiltered = activeSectionId === 'all'
      ? allArticles
      : allArticles.filter(article => article.categories.some(category =>
          category.toLowerCase().replace(/\s+/g, '-') === activeSectionId
        ))

    return categoryFiltered.filter(article => articleMatchesSearch(article, searchQuery))
  }, [activeSectionId, allArticles, searchQuery])

  const activeArticle = allArticles.find(article => article.id === activeArticleId) ?? allArticles[0]

  const handleSelectArticle = (articleId: string) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set('article', articleId)
    nextSearchParams.delete('section')
    setActiveArticleId(articleId)
    setActiveSectionId('all')
    setSearchParams(nextSearchParams, { replace: true })
  }

  const handleSelectSection = (sectionId: LearnSectionId | 'all') => {
    const sectionArticles = sectionId === 'all'
      ? allArticles
      : allArticles.filter(article => article.categories.some(category =>
          category.toLowerCase().replace(/\s+/g, '-') === sectionId
        ))
    const nextArticle = sectionArticles.find(article => articleMatchesSearch(article, searchQuery)) ?? sectionArticles[0] ?? allArticles[0]
    const nextSearchParams = new URLSearchParams(searchParams)

    if (sectionId === 'all') {
      nextSearchParams.delete('section')
    } else {
      nextSearchParams.set('section', sectionId)
    }

    if (nextArticle) {
      nextSearchParams.set('article', nextArticle.id)
      setActiveArticleId(nextArticle.id)
    }

    setActiveSectionId(sectionId)
    setSearchParams(nextSearchParams, { replace: true })
  }

  usePageMetadata({
    title: seo.title,
    description: seo.description,
    path: '/learn',
    locale,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: seo.title,
      description: seo.description,
      url: `${window.location.origin}/learn`,
    },
  })

  return (
    <main className={`min-h-screen bg-white pt-14 text-neutral-900 ${fontClass}`}>
      <div className="mx-auto max-w-[1240px] px-5 py-12 md:px-8 lg:px-10 lg:py-16">
        {activeArticle && content ? (
          <section className="grid gap-12 xl:grid-cols-[minmax(0,1fr)_340px] xl:gap-12">
            <article className="min-w-0">
              <div className="border-t border-neutral-300 pt-6">
                <p className="text-sm uppercase tracking-[0.22em] text-neutral-500">
                  {activeArticle.categories.join(' / ')} · {formatPublishedDate(activeArticle.publishedAt)}
                </p>
                <h1 className="mt-5 max-w-5xl text-[clamp(2.35rem,4.2vw,4.35rem)] font-medium leading-[1.12] tracking-tight text-neutral-950">
                  {activeArticle.title}
                </h1>
                <p className="mt-6 max-w-3xl text-lg leading-[1.85] text-neutral-500">
                  {activeArticle.excerpt}
                </p>
              </div>

              {activeArticle.imageUrl ? (
                <div className="mt-10 overflow-hidden bg-neutral-100">
                  <img
                    src={activeArticle.imageUrl}
                    alt={activeArticle.imageAlt ?? activeArticle.title}
                    className="aspect-[16/9] w-full object-cover"
                  />
                </div>
              ) : null}

              <div
                className="learn-article-body mt-10"
                dangerouslySetInnerHTML={{ __html: activeArticle.contentHtml }}
              />

              <Link
                to={activeArticle.href}
                target="_blank"
                rel="noreferrer"
                className="mt-10 inline-flex border-b border-neutral-950 pb-1 text-base font-medium text-neutral-950 transition hover:text-neutral-500"
              >
                {content.readMoreLabel}
              </Link>
            </article>

            <aside className="xl:sticky xl:top-36 xl:self-start">
              <div className="border-y border-neutral-200 py-6">
                <label className="block text-xs font-medium uppercase tracking-[0.22em] text-neutral-400" htmlFor="learn-search">
                  {content.searchPlaceholder}
                </label>
                <div className="mt-4 flex items-center gap-3 border-b border-neutral-300 pb-3 transition focus-within:border-neutral-950">
                  <LuSearch className="shrink-0 text-neutral-400" size={18} />
                  <input
                    id="learn-search"
                    type="search"
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                    placeholder="Search title, topic, or date"
                    className="min-w-0 flex-1 bg-transparent text-[1.05rem] text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                  />
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="border-0 bg-transparent p-0 text-neutral-400 transition hover:text-neutral-900"
                      aria-label="Clear search"
                    >
                      <LuX size={18} />
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-neutral-200 pb-8">
                <button
                  type="button"
                  onClick={() => handleSelectSection('all')}
                  className={`relative border-0 bg-transparent px-0 pb-1 text-sm font-medium transition ${activeSectionId === 'all' ? 'text-neutral-950 after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:bg-neutral-950' : 'text-neutral-400 hover:text-neutral-900'}`}
                >
                  All
                </button>
                {content.sections.map(section => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleSelectSection(section.id)}
                    className={`relative border-0 bg-transparent px-0 pb-1 text-sm font-medium transition ${section.id === activeSectionId ? 'text-neutral-950 after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:bg-neutral-950' : 'text-neutral-400 hover:text-neutral-900'}`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>

              <div className="mt-10">
                <div className="flex items-end justify-between gap-4">
                  <h2 className="text-[1.45rem] font-medium tracking-tight text-neutral-950">{content.recentPostsTitle}</h2>
                  <p className="text-xs uppercase tracking-[0.16em] text-neutral-400">{visibleArticles.length}/{allArticles.length}</p>
                </div>

                <div className="mt-5 divide-y divide-neutral-200 border-y border-neutral-200">
                  {visibleArticles.map((article, index) => (
                    <button
                      key={article.id}
                      type="button"
                      onClick={() => handleSelectArticle(article.id)}
                      className={`group grid w-full grid-cols-[2.35rem_minmax(0,1fr)] gap-3 border-0 bg-transparent px-0 py-5 text-left transition ${article.id === activeArticle.id ? 'text-neutral-950' : 'text-neutral-500 hover:text-neutral-950'}`}
                    >
                      <span className={`pt-1 text-xs tabular-nums tracking-[0.18em] transition ${article.id === activeArticle.id ? 'text-neutral-950' : 'text-neutral-300 group-hover:text-neutral-500'}`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span>
                        <span className="block text-xs uppercase tracking-[0.16em] text-neutral-400">
                          {article.publishedAt}
                        </span>
                        <span className="mt-2 block text-base leading-7">
                          {article.title}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>

                {visibleArticles.length === 0 ? (
                  <p className="mt-5 border border-dashed border-neutral-300 px-4 py-4 text-sm leading-6 text-neutral-500">
                    No articles match this search.
                  </p>
                ) : null}
              </div>
            </aside>
          </section>
        ) : null}

        {!content ? (
          <div className="border border-dashed border-neutral-300 px-6 py-4 text-sm text-neutral-500">
            Loading learn content...
          </div>
        ) : null}
      </div>
    </main>
  )
}

export default LearnPage
